import React from "react";
import { Link } from "react-router-dom";
import MarketingPageLayout, { PolicySection } from "@/components/marketing/MarketingPageLayout";
import { ShieldCheck, KeyRound, FileLock2, BookCheck, AlertTriangle } from "lucide-react";

export default function ComplianceFramework() {
  return (
    <MarketingPageLayout
      eyebrow="Compliance"
      title="Compliance Framework"
      subtitle="The pillars GuestIntel's product and engineering teams build against — and the controls auditors can verify."
      lastUpdated="February 2026"
      path="/compliance-framework"
      seoDescription="GuestIntel's compliance framework — privacy by design, strong identity, evidence-based records, audit retention, and alignment with GDPR, SOC 2, and ISO 27001."
    >
      <PolicySection title="1. The four pillars">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 not-prose">
          {[
            { icon: ShieldCheck, title: "Privacy by design",  body: "Intel Shield™ matching, encryption at rest, PII reveal with reason." },
            { icon: KeyRound,    title: "Strong identity",    body: "MFA enforced for privileged roles; passkeys + TOTP supported." },
            { icon: FileLock2,   title: "Evidence-based",     body: "Every record verified by case managers before cross-tenant visibility." },
            { icon: BookCheck,   title: "Auditable always",   body: "7-year audit retention; every PII access logged with a typed reason." },
          ].map(({ icon: I, title, body }) => (
            <div key={title} className="border border-zinc-200 rounded-md p-4">
              <I className="w-5 h-5 text-teal mb-2" />
              <div className="font-display font-bold">{title}</div>
              <p className="text-sm text-zinc-700 mt-1">{body}</p>
            </div>
          ))}
        </div>
      </PolicySection>

      <PolicySection title="2. Standards we align with">
        <ul>
          <li><strong>GDPR</strong> — data-subject rights honoured via the <Link to="/gdpr">GDPR Rights portal</Link>; lawful bases documented in our <Link to="/privacy">Privacy Policy</Link>.</li>
          <li><strong>SOC 2 (in progress)</strong> — Type I audit scheduled Q2 2026; Type II planned for Q4 2026.</li>
          <li><strong>ISO 27001 (target)</strong> — Statement of Applicability published Q3 2026.</li>
          <li><strong>PCI DSS</strong> — Out of scope. Payments are tokenised by Stripe; no card data is stored on our infrastructure.</li>
        </ul>
      </PolicySection>

      <PolicySection title="3. Severity rubric">
        <p>Reviewers calibrate severity on four dimensions, each scored 0–3:</p>
        <ul>
          <li><strong>Financial impact</strong> — direct costs to the property (damage, chargeback, lost revenue).</li>
          <li><strong>Repeatability</strong> — likelihood the same pattern recurs elsewhere.</li>
          <li><strong>Safety risk</strong> — threat to staff or other guests.</li>
          <li><strong>Evidence strength</strong> — quality and number of supporting artefacts.</li>
        </ul>
        <p>The aggregate score maps to Level 1 (low), Level 2 (medium), Level 3 (high), Level 4 (critical).</p>
      </PolicySection>

      <PolicySection title="4. Incident response">
        <p className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-1 shrink-0" />
          <span>
            We classify product incidents on the same 1–4 scale. Sev-1 incidents trigger an immediate
            customer-comms cycle, a public post-mortem within 14 days, and a regulatory notification where
            required (GDPR Art. 33: 72 hours).
          </span>
        </p>
      </PolicySection>

      <PolicySection title="5. Sub-processors">
        <p>
          We publish a living list of sub-processors on the <Link to="/trust">Trust Center</Link>. Tenants
          can subscribe to change notifications and object before any new sub-processor is enabled.
        </p>
      </PolicySection>

      <PolicySection title="6. Auditor access">
        <p>
          Operating tenants on the Enterprise tier may request an SOC 2 report under NDA via{" "}
          <a href="mailto:trust@guestintel.net">trust@guestintel.net</a>.
        </p>
      </PolicySection>
    </MarketingPageLayout>
  );
}
